/* Modern Bold — minimal vanilla JS for hero slideshow + featured tabs.
   No framework. Initialised on DOMContentLoaded; idempotent for theme editor. */

/**
 * window.mbFormatMoney(cents) → string
 * ────────────────────────────────────────────────────────────────────
 * Project-wide money formatter, mirrors Shopify's
 * `money_without_trailing_zeros` filter so JS-updated prices stay
 * byte-identical to the server-rendered ones (no flicker on first
 * variant change).
 *
 * Rules:
 *  • Round dollar amounts drop the `.00`  ($40.00 → $40)
 *  • Real fractional cents render at 2 dp ($40.50 stays $40.50)
 *  • Currency is read from window.Shopify.currency.active (Shopify
 *    exposes this on every storefront page) — defaults to USD if the
 *    storefront global is missing (theme-editor preview, tests).
 *  • Uses Intl.NumberFormat so we don't have to ship Shopify's legacy
 *    option_selection.js for `Shopify.formatMoney`, and we get proper
 *    locale-aware currency symbols out of the box.
 *
 * Available before any other modern-* script runs because this file is
 * deferred-loaded ahead of modern-product.js, modern-collection.js, etc.
 * Consumers should treat it as a hard dependency: `window.mbFormatMoney`
 * is always there on the storefront.
 */
window.mbFormatMoney = function (cents) {
  var dollars = cents / 100;
  var hasFraction = dollars % 1 !== 0;
  var ccy = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: ccy,
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(dollars);
  } catch (_) {
    // Unknown currency code → fall back to a plain `$N` so prices
    // never render as raw cents.
    return '$' + dollars.toLocaleString('en-US', {
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: 2,
    });
  }
};

(function () {
  'use strict';

  function initHero(root) {
    if (root.dataset.mbInit) return;
    root.dataset.mbInit = '1';
    var slides = root.querySelectorAll('.mb-hero__slide');
    var dots   = root.querySelectorAll('.mb-hero__dot');
    var prev   = root.querySelector('.mb-hero__arrow--prev');
    var next   = root.querySelector('.mb-hero__arrow--next');
    if (!slides.length) return;
    var i = 0;
    function go(n) {
      i = (n + slides.length) % slides.length;
      slides.forEach(function (s, k) { s.classList.toggle('is-active', k === i); });
      dots.forEach(function (d, k) {
        d.classList.toggle('is-active', k === i);
        d.setAttribute('aria-selected', k === i ? 'true' : 'false');
      });
    }
    dots.forEach(function (d, k) { d.addEventListener('click', function () { go(k); }); });
    if (prev) prev.addEventListener('click', function () { go(i - 1); });
    if (next) next.addEventListener('click', function () { go(i + 1); });

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var auto = root.dataset.autoplay === 'true' && !reduced;
    if (auto) {
      var ms = parseInt(root.dataset.interval, 10) || 6000;
      var t = setInterval(function () { go(i + 1); }, ms);
      root.addEventListener('mouseenter', function () { clearInterval(t); });
    }
  }

  function initTabs(root) {
    if (root.dataset.mbInit) return;
    root.dataset.mbInit = '1';
    var btns = root.querySelectorAll('.mb-tabs__btn');
    var panels = root.querySelectorAll('.mb-tabs__panel');
    function activate(idx) {
      btns.forEach(function (b, k) {
        var on = k === idx;
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });
      panels.forEach(function (p, k) { p.classList.toggle('is-active', k === idx); });
    }
    btns.forEach(function (b, k) {
      b.addEventListener('click', function () { activate(k); });
      b.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); var n = (k + 1) % btns.length; btns[n].focus(); activate(n); }
        if (e.key === 'ArrowLeft')  { e.preventDefault(); var p = (k - 1 + btns.length) % btns.length; btns[p].focus(); activate(p); }
      });
    });
  }

  // Header dropdown — touch/keyboard click-toggle (hover already handled by CSS).
  // Closes on outside click and ESC. Accessible aria-expanded reflects state.
  function initDropdowns() {
    var items = document.querySelectorAll('[data-mb-dropdown]');
    if (!items.length) return;
    items.forEach(function (item) {
      if (item.dataset.mbDropdownInit) return;
      item.dataset.mbDropdownInit = '1';
      var trigger = item.querySelector('.mb-header__nav-link');
      if (!trigger) return;
      trigger.addEventListener('click', function (e) {
        // Only intercept when the link target is "#" or empty — let real
        // category-landing links navigate; the dropdown shows on hover anyway.
        var href = trigger.getAttribute('href') || '';
        if (href && href !== '#' && window.matchMedia('(hover: hover)').matches) return;
        e.preventDefault();
        var open = item.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        // Close siblings
        items.forEach(function (other) {
          if (other !== item) {
            other.classList.remove('is-open');
            var t = other.querySelector('.mb-header__nav-link');
            if (t) t.setAttribute('aria-expanded', 'false');
          }
        });
      });
    });
    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-mb-dropdown]')) {
        items.forEach(function (item) {
          item.classList.remove('is-open');
          var t = item.querySelector('.mb-header__nav-link');
          if (t) t.setAttribute('aria-expanded', 'false');
        });
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      items.forEach(function (item) {
        if (item.classList.contains('is-open')) {
          item.classList.remove('is-open');
          var t = item.querySelector('.mb-header__nav-link');
          if (t) { t.setAttribute('aria-expanded', 'false'); t.focus(); }
        }
      });
    });
  }

  // Publish the modern header's real pixel height as --mb-header-height on
  // :root so callers (PDP gallery, sticky offsets, etc.) can subtract it
  // from 100vh without hard-coding a value. ResizeObserver keeps it fresh
  // when the header re-flows (font load, mobile padding change, locale text
  // wrap). Idempotent: bound once on first call.
  var _headerHeightBound = false;
  function setupHeaderHeightVar() {
    var header = document.querySelector('.mb-header');
    if (!header) return;
    function publish() {
      document.documentElement.style.setProperty(
        '--mb-header-height',
        header.offsetHeight + 'px'
      );
    }
    publish();
    if (_headerHeightBound) return;
    _headerHeightBound = true;
    window.addEventListener('resize', publish);
    window.addEventListener('load', publish);
    if ('ResizeObserver' in window) {
      new ResizeObserver(publish).observe(header);
    }
  }

  // Mobile burger menu — toggles .is-open on the primary nav and locks
  // body scroll while the drawer is open. ESC and outside-tap also
  // close. Idempotent: dataset flag stops re-binding when the section
  // reloads in the theme editor.
  function initBurger() {
    var btn = document.querySelector('[data-mb-burger]');
    if (!btn || btn.dataset.mbInit) return;
    btn.dataset.mbInit = '1';
    var nav = document.getElementById(btn.getAttribute('aria-controls'));
    var overlay = document.querySelector('[data-mb-burger-overlay]');
    if (!nav) return;

    // Note: nav stays where Liquid rendered it (inside <header>). An
    // earlier version portal'd it to <body> for mobile, but that broke
    // desktop — the header's nav links would render and then disappear
    // as soon as initBurger ran. position:fixed works against the
    // viewport regardless of the sticky header section's stacking
    // context (sticky alone doesn't create a transform/containing
    // block), so the drawer doesn't need the portal.

    function setOpen(open) {
      nav.classList.toggle('is-open', open);
      if (overlay) overlay.classList.toggle('is-open', open);
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      btn.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      document.body.classList.toggle('mb-burger-open', open);
    }

    btn.addEventListener('click', function () {
      setOpen(!nav.classList.contains('is-open'));
    });
    // Backdrop tap closes drawer
    if (overlay) {
      overlay.addEventListener('click', function () { setOpen(false); });
    }
    // In-drawer close button
    var closeBtn = nav.querySelector('[data-mb-burger-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () { setOpen(false); });
    }
    // Close on ESC
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && nav.classList.contains('is-open')) {
        setOpen(false);
        btn.focus();
      }
    });
    // Close when a nav link is followed
    nav.addEventListener('click', function (e) {
      var link = e.target.closest('a');
      if (link && link.getAttribute('href') && link.getAttribute('href') !== '#') {
        setOpen(false);
      }
    });
  }

  // Desktop nav uses hover-controlled dropdowns (opacity + visibility)
  // but those CSS rules need the submenu in the layout — and a <details>
  // browser-natively hides its non-summary content while [open] is
  // absent, regardless of CSS. So on ≥990px we force the [open]
  // attribute on every parent <details> so the submenu renders, and
  // remove it on <990px so the mobile drawer's tap-to-toggle works.
  // matchMedia.change keeps the two states in sync if the viewport
  // crosses the breakpoint (orientation flip, devtools resize).
  function syncNavDetailsOpen() {
    var desktop = window.matchMedia('(min-width: 990px)').matches;
    document.querySelectorAll('.mb-header__nav .mb-header__nav-item--has-children').forEach(function (d) {
      if (desktop) d.setAttribute('open', '');
      else d.removeAttribute('open');
    });
  }
  if (window.matchMedia) {
    var mql = window.matchMedia('(min-width: 990px)');
    if (mql.addEventListener) mql.addEventListener('change', syncNavDetailsOpen);
    else if (mql.addListener) mql.addListener(syncNavDetailsOpen); // Safari < 14
  }

  function initAll() {
    document.querySelectorAll('[data-mb-hero]').forEach(initHero);
    document.querySelectorAll('[data-mb-tabs]').forEach(initTabs);
    initDropdowns();
    setupHeaderHeightVar();
    initBurger();
    syncNavDetailsOpen();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
